/**
 * Creates a debounced version of the provided function.
 * @param func - The function to be debounced.
 * @param delay - The delay in milliseconds before the function is executed.
 * @returns A debounced version of the function that returns a Promise.
 * == Example Usage ==
 * const debouncedFetch = debounce((a: string) => {...}, 500);
 * debouncedFetch('test')
 * debouncedFn.cancel() // 手動取消執行
 */

export function debounce<T extends unknown[]>(func: (...args: T) => void, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: T) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

/**
 * 將字串中的「台」統一轉換為「臺」，用於比對地址、分行名稱等資料時消除異體字造成的差異。
 * @param text 原始字串
 * @returns 「台」已全部替換為「臺」的字串
 */
export function normalizeTaiwanCharacter(text: string): string {
  return text.replace(/台/g, "臺");
}

/**
 * Creates a debounced version of the provided function.
 * @param func - The function to be debounced.
 * @param delay - 延遲時間(ms)
 * @return A debounced version of the function that returns a Promise.
 * == Example Usage ==
 * const debouncedFetch = debounceAsync(async (a: string, b: number) => {
 *     return await fetchSomething(a, b);
 *   }, 500);
 * debouncedFetch("param1", 123);
 * debouncedFetch.cancel(); // 手動取消執行
 * */
export function debounceAsync<T extends unknown[], U>(func: (...args: T) => PromiseLike<U> | U, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: T): Promise<U> => {
    if (timeoutId) clearTimeout(timeoutId);
    return new Promise<U>((resolve) => {
      timeoutId = setTimeout(() => {
        timeoutId = null;
        resolve(func(...args));
      }, delay);
    });
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}
